import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {type IdentityLabel} from '#/lib/moderation'
import {STALE} from '#/state/queries'
import {useSession} from '#/state/session'

// Cloudflare Worker URL for invite/preferences system
const INVITE_WORKER_URL = 'https://batesky-invite-system.attps.workers.dev'

export type UserPreferencesResponse = {
  showMeIdentities: IdentityLabel[]
}

const userPreferencesQueryKeyRoot = 'userPreferences'

export function createUserPreferencesQueryKey(userDid: string | undefined) {
  return [userPreferencesQueryKeyRoot, userDid]
}

export function useUserPreferencesQuery() {
  const {currentAccount} = useSession()
  const userDid = currentAccount?.did

  return useQuery({
    enabled: !!userDid,
    staleTime: STALE.MINUTES.FIVE,
    queryKey: createUserPreferencesQueryKey(userDid),
    queryFn: async (): Promise<UserPreferencesResponse> => {
      if (!userDid) {
        return {showMeIdentities: []}
      }

      try {
        const response = await fetch(
          `${INVITE_WORKER_URL}/preferences?userDid=${encodeURIComponent(userDid)}`,
        )

        if (!response.ok) {
          // Return empty if no preferences found
          return {showMeIdentities: []}
        }

        const data = await response.json()
        return {
          showMeIdentities: data.showMeIdentities || [],
        }
      } catch (error) {
        console.error('Error fetching user preferences:', error)
        return {showMeIdentities: []}
      }
    },
  })
}

export function useUpdateUserPreferencesMutation() {
  const {currentAccount} = useSession()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (showMeIdentities: IdentityLabel[]) => {
      if (!currentAccount?.did || !currentAccount?.handle) {
        throw new Error('Not logged in')
      }

      const response = await fetch(`${INVITE_WORKER_URL}/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userDid: currentAccount.did,
          username: currentAccount.handle,
          showMeIdentities,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to save preferences')
      }

      return response.json()
    },
    onSuccess: (_data, variables) => {
      // Update the cache with the new values
      queryClient.setQueryData(
        createUserPreferencesQueryKey(currentAccount?.did),
        {showMeIdentities: variables},
      )
    },
  })
}
